// Web mock for react-native-progress
import React from 'react';
import { View, StyleSheet } from 'react-native';

interface BarProps {
  progress?: number;
  indeterminate?: boolean;
  width?: number | null;
  height?: number;
  color?: string;
  unfilledColor?: string;
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
  style?: any;
  animated?: boolean;
  useNativeDriver?: boolean;
  animationType?: string;
  animationConfig?: any;
}

const Bar: React.FC<BarProps> = ({
  progress = 0,
  indeterminate = false,
  width = null,
  height = 6,
  color = '#E30613',
  unfilledColor = '#E0E0E0',
  borderWidth = 0,
  borderColor,
  borderRadius = 4,
  style,
}) => {
  return (
    <View
      style={[
        {
          width: width || '100%',
          height,
          backgroundColor: unfilledColor,
          borderRadius,
          borderWidth,
          borderColor: borderColor || color,
          overflow: 'hidden',
        },
        style,
      ]}
    >
      <View
        style={{
          width: indeterminate ? '50%' : `${Math.min(progress * 100, 100)}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius,
        }}
      />
    </View>
  );
};

interface CircleProps {
  progress?: number;
  size?: number;
  thickness?: number;
  color?: string;
  unfilledColor?: string;
  borderWidth?: number;
  style?: any;
  children?: React.ReactNode;
  showsText?: boolean;
  formatText?: (progress: number) => string;
  textStyle?: any;
}

const Circle: React.FC<CircleProps> = ({
  progress = 0,
  size = 40,
  thickness = 3,
  color = '#E30613',
  unfilledColor = '#E0E0E0',
  style,
  children,
  showsText = false,
  formatText,
  textStyle,
}) => {
  const radius = (size - thickness) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - progress * circumference;

  return (
    <View style={[{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }, style]}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={unfilledColor}
          strokeWidth={thickness}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={thickness}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {showsText && (
        <View style={StyleSheet.absoluteFill as any}>
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            {children}
          </View>
        </View>
      )}
    </View>
  );
};

interface PieProps {
  progress?: number;
  size?: number;
  color?: string;
  unfilledColor?: string;
  borderWidth?: number;
  borderColor?: string;
  style?: any;
}

const Pie: React.FC<PieProps> = ({
  progress = 0,
  size = 40,
  color = '#E30613',
  unfilledColor = '#E0E0E0',
  style,
}) => {
  return (
    <Circle
      progress={progress}
      size={size}
      color={color}
      unfilledColor={unfilledColor}
      style={style}
    />
  );
};

export { Bar, Circle, Pie };
export default { Bar, Circle, Pie };
